import React, { useState } from 'react';

import {
  Archive, Delete, KeyboardDoubleArrowUp, MoreVert, Restore,
} from '@mui/icons-material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CloseIcon from '@mui/icons-material/Close';
import {
  Grid,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import dayjs from 'dayjs';
import RelativeTime from 'dayjs/plugin/relativeTime';

import { UserNote } from '../../services/users/types';
import Chip from '../Chip';
import ContentCard from '../ContentCard';
import EditField from '../EditField';
import { IconDatePicker } from '../IconDatePicker';
import Link from '../Link';

interface NotesCardProps {
  note: UserNote;
  onNoteDelete?: (noteCreationDatetime: number) => void,
  onNoteArchive?: (noteCreationDatetime: number) => void,
  onNoteRestore?: (noteCreationDatetime: number) => void,
  onNotePromote?: (noteCreationDatetime: number) => void,
  onNoteBillingRelatedToggle?: (noteCreationDatetime: number, billingRelated: boolean) => void,
  onNoteUpdate?: (noteCreationDatetime: number, value: string) => void,
  onNoteDueDateChange?: (noteCreationDatetime: number, value: number) => void,
  onNoteAssigneeChange?: (noteCreationDatetime: number, value: string) => void,
  assigneeOptions?: {
    value: string;
    label: string;
  }[];
  isNoteUpdateLoading?: boolean;
  userName?: string;
  userDetails?: string;
  userDetailsHref?: string;
}

dayjs.extend(RelativeTime);

// TODO: Use UpdateLocale to custom format relative time

const NoteCard = ({
  note,
  onNoteDelete,
  onNoteArchive,
  onNoteRestore,
  onNotePromote,
  onNoteBillingRelatedToggle,
  onNoteUpdate,
  onNoteDueDateChange,
  onNoteAssigneeChange,
  assigneeOptions,
  isNoteUpdateLoading,
  userName,
  userDetails,
  userDetailsHref,
}: NotesCardProps) => {
  const theme = useTheme();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const getDueDateMessage = () => {
    if (!note?.dueDatetime) return 'No Due Date';
    if (dayjs(note.dueDatetime) <= dayjs().endOf('day')) {
      const daysOverdue = dayjs(note.dueDatetime).diff(dayjs().endOf('day'), 'day');
      if (daysOverdue === 0) return ' (Due today)';
      if (daysOverdue < 0) return ` (${Math.abs(daysOverdue)} days overdue)`;
    }
    return '';
  };

  return (
    <ContentCard padding="small">
      <Grid container spacing={1} alignItems="center">
        <Grid item xs={12} container justifyContent="space-between">
          <Typography variant="caption" color={theme.palette.gray.main}>
            {`Created${note?.creatorName ? ` by ${note.creatorName}` : ''}`}
          </Typography>
          <Typography variant="caption" color={theme.palette.gray.main}>
            {// Get how many days or hours ago the note was created e.g. 2d ago or 2h ago
              dayjs(note.creationDatetime).fromNow()
            }
          </Typography>
        </Grid>
        {(userName || userDetails || userDetailsHref) && (
          <Grid item xs={12} container justifyContent="space-between">
            <Typography variant="h5">
              {userDetailsHref ? (
                <Link href={userDetailsHref}>
                  {userName || 'No Name'}
                </Link>
              ) : userName || 'No Name'}
            </Typography>
            {userDetails && (
              <Chip label={userDetails} size="small" variant="outlined" />
            )}
          </Grid>
        )}
        <Grid item xs={12}>
          <EditField
            value={note.note}
            onSubmit={(value) => {
              if (onNoteUpdate) onNoteUpdate(note.creationDatetime, value as string);
            }}
            type="long"
            disabled={!note.active || isNoteUpdateLoading || !onNoteUpdate}
          />
        </Grid>
        {
          note?.billingRelated && (
            <Grid item xs={12}>
              <Chip
                label="Billing Related"
                size="small"
                variant="outlined"
                icon={<AttachMoneyIcon color="orange" />}
                color="orange"
              />
            </Grid>
          )
        }
        <Grid item xs={5} md={5}>
          {assigneeOptions && onNoteAssigneeChange && (
            <Stack direction="row" alignItems="center">
              <IconButton disableRipple>
                <AccountCircleIcon />
              </IconButton>
              <EditField
                value={note?.assignee}
                type="select"
                typeOptions={[
                  {
                    label: 'Unassigned',
                    value: 'UNASSIGNED',
                  },
                  ...assigneeOptions,
                ]}
                typographyProps={note?.assignee === 'UNASSIGNED' ? {
                  color: theme.palette.gray.main,
                } : {}}
                onSubmit={(value) => onNoteAssigneeChange(
                  note.creationDatetime,
                  value as string,
                )}
                disabled={!note.active || isNoteUpdateLoading}
              />
            </Stack>
          )}
        </Grid>
        <Grid item xs={6} md={6}>
          {onNoteDueDateChange && (
            <Stack direction="row" alignItems="center">
              <IconDatePicker
                value={dayjs(note?.dueDatetime || dayjs().valueOf())}
                onChange={(newDate) => onNoteDueDateChange(
                  note.creationDatetime,
                  newDate?.valueOf() ?? 0,
                )}
                disabled={!note.active || isNoteUpdateLoading}
              />
              <Typography
                color={(!note.dueDatetime || dayjs(note.dueDatetime) > dayjs().endOf('day')) ? 'default' : 'error'}
                fontWeight={(!note.dueDatetime || dayjs(note.dueDatetime) > dayjs().endOf('day')) ? 'default' : 'bold'}
              >
                {note?.dueDatetime ?
                  `${dayjs(note.dueDatetime).format('D MMM \'YY')} 
                  ${getDueDateMessage()}`
                  : 'No Due Date'}
              </Typography>
              {!!note?.dueDatetime && (
                <IconButton
                  color={(dayjs(note.dueDatetime) > dayjs().endOf('day')) ? 'default' : 'error'}
                  onClick={() => onNoteDueDateChange(
                    note.creationDatetime,
                    0,
                  )}
                >
                  <CloseIcon />
                </IconButton>
              )}
            </Stack>
          )}
        </Grid>
        {(onNotePromote || onNoteArchive || onNoteDelete || onNoteRestore) && (
          <Grid item xs={1} md={1} container justifyContent="flex-end">
            <IconButton onClick={handleClick}>
              <MoreVert />
            </IconButton>
            {note.active ? (

              <Menu
                id="demo-positioned-menu"
                aria-labelledby="demo-positioned-button"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: 'center',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
              >
                {onNoteBillingRelatedToggle && (
                  <MenuItem onClick={() => {
                    onNoteBillingRelatedToggle(note.creationDatetime, !note.billingRelated);
                    handleClose();
                  }}
                  >
                    <ListItemIcon>
                      <AttachMoneyIcon color="orange" />
                    </ListItemIcon>
                    <ListItemText>
                      {note.billingRelated ? 'Unmark as billing related' : 'Mark as billing related'}
                    </ListItemText>
                  </MenuItem>
                )}
                {onNotePromote && (
                  <MenuItem onClick={() => {
                    onNotePromote(note.creationDatetime);
                    handleClose();
                  }}
                  >
                    <ListItemIcon>
                      <KeyboardDoubleArrowUp color="blue" />
                    </ListItemIcon>
                    <ListItemText>
                      Promote
                    </ListItemText>
                  </MenuItem>
                )}
                {onNoteArchive && (
                  <MenuItem onClick={() => {
                    onNoteArchive(note.creationDatetime);
                    handleClose();
                  }}
                  >
                    <ListItemIcon>
                      <Archive color="gray" />
                    </ListItemIcon>
                    <ListItemText>
                      Archive
                    </ListItemText>
                  </MenuItem>
                )}
              </Menu>
            ) : (

              <Menu
                id="demo-positioned-menu"
                aria-labelledby="demo-positioned-button"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: 'center',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
              >
                {onNoteRestore && (
                  <MenuItem onClick={() => {
                    onNoteRestore(note.creationDatetime);
                    handleClose();
                  }}
                  >
                    <ListItemIcon>
                      <Restore color="green" />
                    </ListItemIcon>
                    <ListItemText>
                      Restore
                    </ListItemText>
                  </MenuItem>
                )}
                {onNoteDelete && (
                  <MenuItem onClick={() => {
                    onNoteDelete(note.creationDatetime);
                    handleClose();
                  }}
                  >
                    <ListItemIcon>
                      <Delete color="red" />
                    </ListItemIcon>
                    <ListItemText>
                      Delete
                    </ListItemText>
                  </MenuItem>
                )}
              </Menu>
            )}
          </Grid>
        )}
      </Grid>
    </ContentCard>
  );
};
export default NoteCard;
